import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles, X, Send, Trash2, Loader2, ShoppingCart, ArrowRight, Headset, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, stripMd } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { getAiSession, resetAiSession, trackActivity } from "@/hooks/useAi";

const HIDE_PREFIXES = ["/admin", "/vendeur", "/mes-formations"];

const QUICK = [
  "Je cherche une licence Windows",
  "Quelle formation pour apprendre Photoshop ?",
  "Comment payer ma commande ?",
  "Suivre ma commande",
];

function RecoCard({ card }) {
  const { addToCart, currency } = useStore();
  const isProduct = card.kind === "product";
  return (
    <div
      data-testid="ai-reco-card"
      className="overflow-hidden rounded-2xl border border-line bg-white shadow-sm"
    >
      <div className="flex gap-3 p-2.5">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-surface">
          {card.image_url ? (
            <img src={fileUrl(card.image_url)} alt={card.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-violet-400">
              <Sparkles className="h-6 w-6" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold text-ink">{card.title}</p>
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-500">{card.description}</p>
          <p className="mt-1 text-sm font-black text-violet-700">{formatPrice(card.price, currency)}</p>
        </div>
      </div>
      <div className="flex border-t border-line">
        <Link
          to={card.url}
          data-testid="ai-reco-view"
          className="flex flex-1 items-center justify-center gap-1 py-2 text-[11px] font-bold text-violet-700 transition-colors hover:bg-violet-50"
        >
          {card.cta || "Voir"} <ArrowRight className="h-3 w-3" />
        </Link>
        {isProduct && (
          <button
            data-testid="ai-reco-add"
            onClick={() => {
              addToCart({ id: card.id, name: card.title, slug: card.slug, price: card.price, old_price: card.old_price, image_url: card.image_url });
              trackActivity({ event_type: "add_to_cart", product_slug: card.slug });
            }}
            className="flex flex-1 items-center justify-center gap-1 border-l border-line py-2 text-[11px] font-bold text-ink transition-colors hover:bg-surface"
          >
            <ShoppingCart className="h-3 w-3" /> Panier
          </button>
        )}
      </div>
    </div>
  );
}

export default function AiWidget() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [emailPrompt, setEmailPrompt] = useState(false);
  const [email, setEmail] = useState("");
  const scrollRef = useRef(null);
  const lastPath = useRef("");

  const routeContext = useCallback(() => {
    const parts = pathname.split("/").filter(Boolean);
    const ctx = { page: pathname };
    if (parts[0] === "boutique" && parts[1]) ctx.product_slug = parts[1];
    if (parts[0] === "formations" && parts[1]) ctx.formation_slug = parts[1];
    if (parts[0] === "boutique-vendeur" && parts[1]) ctx.vendor_slug = parts[1];
    return ctx;
  }, [pathname]);

  // suivi d'activité (respect vie privée côté serveur)
  useEffect(() => {
    if (pathname === lastPath.current) return;
    lastPath.current = pathname;
    if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return;
    const ctx = routeContext();
    let event_type = "page_view";
    if (ctx.product_slug) event_type = "product_view";
    else if (ctx.formation_slug) event_type = "formation_view";
    trackActivity({ event_type, page: pathname, product_slug: ctx.product_slug || "", formation_slug: ctx.formation_slug || "", vendor_slug: ctx.vendor_slug || "" });
  }, [pathname, routeContext]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get(`/ai/session/${getAiSession()}`);
      const msgs = (data.messages || []).map((m) => ({ role: m.role, content: m.content, recommendations: [] }));
      setMessages(msgs);
    } catch (e) {
      setMessages([]);
    }
  }, []);

  const openWidget = () => {
    setOpen(true);
    if (messages.length === 0) {
      loadSession().then(() => {
        setMessages((prev) =>
          prev.length
            ? prev
            : [{ role: "assistant", content: "Bonjour 👋 Je suis ISMO-TECH AI. Je peux vous aider à trouver une licence, une formation, suivre une commande ou payer. Comment puis-je vous aider ?", recommendations: [] }],
        );
      });
    }
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: message, recommendations: [] }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", {
        message,
        session_id: getAiSession(),
        context: routeContext(),
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, recommendations: data.recommendations || [] }]);
    } catch (e) {
      const msg = e?.response?.status === 429
        ? apiError(e)
        : "Désolé, une erreur est survenue. Réessayez dans un instant.";
      setMessages((prev) => [...prev, { role: "assistant", content: msg, recommendations: [] }]);
    } finally {
      setSending(false);
    }
  };

  const clearHistory = async () => {
    try {
      await api.delete(`/ai/session/${getAiSession()}`);
    } catch (e) { /* ignore */ }
    resetAiSession();
    setMessages([{ role: "assistant", content: "Historique effacé. Comment puis-je vous aider ?", recommendations: [] }]);
    toast.success("Historique de conversation effacé");
  };

  const escalate = async (mail) => {
    try {
      const { data } = await api.post("/ai/escalate", {
        session_id: getAiSession(),
        email: mail || "",
        message: "Le client souhaite parler à un conseiller humain.",
      });
      toast.success(`Ticket créé (${data.ticket.reference}). Un conseiller vous recontacte.`);
      setEmailPrompt(false);
      setEmail("");
      setMessages((prev) => [...prev, { role: "assistant", content: `J'ai transmis votre demande à un conseiller. Référence : ${data.ticket.reference}. Vous serez recontacté rapidement. 🙌`, recommendations: [] }]);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handleEscalate = () => {
    if (user) return escalate();
    setEmailPrompt(true);
  };

  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <>
      {/* Bouton flottant */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="ai-widget-toggle"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={openWidget}
            className="fixed bottom-24 right-4 z-[70] flex items-center gap-2 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 p-3 text-white shadow-xl shadow-violet-500/30 lg:bottom-6 lg:right-6 lg:py-3 lg:pl-3 lg:pr-5"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
              <Sparkles className="h-5 w-5" />
            </span>
            <span className="hidden text-sm font-bold lg:inline">Assistant IA</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Fenêtre de chat */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-widget-panel"
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed inset-0 z-[80] flex flex-col bg-white lg:inset-auto lg:bottom-6 lg:right-6 lg:h-[620px] lg:w-[400px] lg:rounded-3xl lg:border lg:border-line lg:shadow-2xl lg:overflow-hidden"
          >
            {/* En-tête */}
            <div className="flex items-center gap-3 bg-gradient-to-br from-violet-600 to-indigo-600 px-4 py-3.5 text-white">
              <button onClick={() => setOpen(false)} className="lg:hidden -ml-1" data-testid="ai-back">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                <Sparkles className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black leading-tight">ISMO-TECH AI</p>
                <p className="flex items-center gap-1 text-[11px] text-white/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> En ligne
                </p>
              </div>
              <button onClick={clearHistory} data-testid="ai-clear" title="Effacer l'historique" className="rounded-lg p-1.5 hover:bg-white/15">
                <Trash2 className="h-4.5 w-4.5" />
              </button>
              <button onClick={() => setOpen(false)} data-testid="ai-close" className="hidden rounded-lg p-1.5 hover:bg-white/15 lg:block">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-surface px-3.5 py-4">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className="max-w-[86%]">
                    <div
                      data-testid={m.role === "user" ? "ai-message-user" : "ai-message-assistant"}
                      className={`whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                        m.role === "user"
                          ? "bg-gradient-to-br from-violet-600 to-indigo-600 text-white"
                          : "border border-line bg-white text-ink"
                      }`}
                    >
                      {stripMd(m.content)}
                    </div>
                    {m.recommendations?.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {m.recommendations.map((c, j) => (
                          <RecoCard key={j} card={c} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-2xl border border-line bg-white px-3.5 py-2.5 text-[13px] text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin text-violet-600" /> ISMO-TECH AI réfléchit…
                  </div>
                </div>
              )}
              {messages.length <= 1 && !sending && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      data-testid="ai-quick-chip"
                      onClick={() => send(q)}
                      className="rounded-full border border-violet-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-violet-700 transition-colors hover:bg-violet-50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Escalade + email */}
            <div className="border-t border-line bg-white px-3.5 pt-2">
              {emailPrompt ? (
                <div className="mb-2 flex items-center gap-2">
                  <input
                    data-testid="ai-escalate-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre email pour être recontacté"
                    className="flex-1 rounded-full border border-line px-3 py-2 text-[13px] outline-none focus:border-violet-500"
                  />
                  <button
                    data-testid="ai-escalate-confirm"
                    onClick={() => escalate(email)}
                    className="rounded-full bg-violet-600 px-3 py-2 text-[12px] font-bold text-white"
                  >
                    Envoyer
                  </button>
                </div>
              ) : (
                <button
                  data-testid="ai-escalate"
                  onClick={handleEscalate}
                  className="mb-2 flex items-center gap-1.5 text-[11px] font-bold text-violet-700 hover:underline"
                >
                  <Headset className="h-3.5 w-3.5" /> Parler à un conseiller
                </button>
              )}
            </div>

            {/* Composer */}
            <div className="flex items-center gap-2 border-t border-line bg-white px-3 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:pb-3">
              <input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrivez votre message…"
                className="flex-1 rounded-full border border-line bg-surface px-4 py-2.5 text-[13px] outline-none focus:border-violet-500"
              />
              <button
                data-testid="ai-send"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white disabled:opacity-50"
              >
                <Send className="h-4.5 w-4.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
